import type {
  BookingEventDetail,
  BookingEventRegistration,
  BookingEventRegistrationEditFormValues,
  BookingEventRegistrationFormValues,
  BookingEventRegistrationMoveFormValues,
  BookingEventService,
} from '@/types';

import { bookingPersonName, toFormString } from '@/helpers';

export const ALL_WAVES = null;

const MIN_SEARCH_LENGTH = 2;

export const registeredIn = (
  event: BookingEventDetail,
  waveId: number | null,
): { service: BookingEventService } | null => {
  if (!event.split_into_waves) {
    return event.service && { service: event.service };
  }

  return event.waves.find((wave) => wave.id === waveId) ?? null;
};

export const buildRegistrationsUrl = (eventsUrl: string, eventId: number) =>
  `${eventsUrl}/${eventId}/registrations`;

const customFieldAnswersAttributes = (values: Record<string, string>) =>
  Object.entries(values)
    .filter(([, value]) => value.trim() !== '')
    .map(([fieldId, value]) => ({
      service_custom_field_id: Number(fieldId),
      value: value.trim(),
    }));

export const createEventRegistrationPayload = (
  values: BookingEventRegistrationFormValues,
  waveId: number | null,
) => ({
  registration: {
    event_wave_id: waveId,
    client_id: values.client_id ? Number(values.client_id) : null,
    guest_info: values.client_id ? null : values.guest_info.trim() || null,
    note: values.note.trim() || null,
    staff_comment: values.staff_comment.trim() || null,
    channel: 'admin',
    custom_field_answers_attributes: customFieldAnswersAttributes(
      values.custom_field_values,
    ),
  },
});

export const updateEventRegistrationPayload = (
  values: BookingEventRegistrationEditFormValues,
) => ({
  registration: {
    staff_comment: values.staff_comment.trim() || null,
    custom_field_answers_attributes: customFieldAnswersAttributes(
      values.custom_field_values,
    ),
  },
});

export const moveEventRegistrationPayload = (
  values: BookingEventRegistrationMoveFormValues,
) => ({
  registration: {
    event_wave_id: Number(values.event_wave_id),
  },
});

export const registrationCustomFieldValues = (
  registration: BookingEventRegistration,
) =>
  Object.fromEntries(
    registration.custom_field_answers.map((answer) => [
      answer.service_custom_field_id,
      toFormString(answer.value),
    ]),
  );

export const registrationsExportUrl = (
  eventsUrl: string,
  event: BookingEventDetail,
  waveId: number | null,
) => {
  const url = `${eventsUrl}/${event.id}/registrations.csv`;

  return event.split_into_waves && waveId !== ALL_WAVES
    ? `${url}?wave_id=${waveId}`
    : url;
};

export const filterRegistrationsByWave = (
  event: BookingEventDetail | undefined,
  waveId: number | null,
) => {
  const registrations = event?.registrations ?? [];

  if (!event?.split_into_waves || waveId === ALL_WAVES) {
    return registrations;
  }

  return registrations.filter(
    (registration) => registration.event_wave_id === waveId,
  );
};

export const searchRegistrations = (
  registrations: BookingEventRegistration[],
  query: string,
) => {
  const trimmedQuery = query.trim().toLocaleLowerCase();

  if (trimmedQuery.length < MIN_SEARCH_LENGTH) {
    return registrations;
  }

  const tokens = trimmedQuery.split(/\s+/);

  return registrations.filter((registration) => {
    const name = bookingPersonName(
      registration.client,
      registration.guest_info,
      '',
    ).toLocaleLowerCase();

    return tokens.every((token) => name.includes(token));
  });
};

export const isRegistrationMovable = (registration: BookingEventRegistration) =>
  registration.move_options.some(
    (wave) => wave.id !== registration.event_wave_id && wave.available,
  );

export const moveOptions = (
  event: BookingEventDetail,
  registration: BookingEventRegistration,
) =>
  registration.move_options
    .filter(({ id }) => id !== registration.event_wave_id)
    .flatMap(({ id, available, error_message }) => {
      const wave = event.waves.find((item) => item.id === id);

      return wave
        ? [{ wave, isAvailable: available, errorMessage: error_message }]
        : [];
    });
